import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Check, X, Loader2, Inbox } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const fmtDay = (d) => { try { return format(parseISO(d), 'EEE d MMM yyyy'); } catch { return d; } };

/** Admin — pending hours change requests from the portal with approve / reject. */
export const HoursChangeRequestsCard = ({ api, onChanged }) => {
  const [items, setItems] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/hours/change-requests?status=${showAll ? 'all' : 'pending'}&limit=100`);
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [api, showAll]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/hours/change-requests/${id}/${action}`, { note: notes[id] || null });
      toast.success(action === 'approve' ? 'Hours updated and the user has been told' : 'Request rejected — the user has been told');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update the request');
    } finally { setBusy(null); }
  };

  const pending = (items || []).filter(i => i.status === 'pending').length;

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="hours-change-requests-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            Hours change requests
            {pending > 0 && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30" data-testid="hours-change-requests-pending">{pending} pending</Badge>}
          </h3>
          <button type="button" onClick={() => setShowAll(v => !v)} className="text-xs text-gray-400 hover:text-white" data-testid="hours-change-requests-toggle">
            {showAll ? 'Show pending only' : 'Show all'}
          </button>
        </div>
        {items === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#F5A623]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm" data-testid="hours-change-requests-empty">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            {showAll ? 'No change requests yet' : 'Nothing waiting for review'}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(r => (
              <div key={r.id} className="rounded-lg bg-[#1E2530] p-3" data-testid={`hours-change-request-${r.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-white font-medium">{r.user_name}</span>
                  <span className="text-[#F5A623] text-sm">{r.project_ref || '—'}</span>
                  <span className="text-gray-400 text-sm">{fmtDay(r.date)}</span>
                  <span className="ml-auto text-sm">
                    <span className="text-gray-500 line-through mr-2">{r.current_hours != null ? `${r.current_hours}h` : '—'}</span>
                    <span className="text-white font-semibold">{r.requested_hours}h</span>
                  </span>
                  {r.status !== 'pending' && (
                    <Badge className={r.status === 'approved' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>{r.status}</Badge>
                  )}
                </div>
                <p className="text-gray-300 text-sm mt-1.5">“{r.reason}”</p>
                {r.status === 'pending' ? (
                  <div className="mt-2 flex flex-col sm:flex-row gap-2">
                    <Textarea value={notes[r.id] || ''} onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))} placeholder="Optional note back to the user" rows={1}
                      className="bg-[#2A3240] border-white/15 text-white text-sm min-h-[38px] flex-1 resize-none" data-testid={`hours-change-note-${r.id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => review(r.id, 'approve')} disabled={!!busy} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`hours-change-approve-${r.id}`}>
                        {busy === r.id + 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => review(r.id, 'reject')} disabled={!!busy} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`hours-change-reject-${r.id}`}>
                        {busy === r.id + 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" /> Reject</>}
                      </Button>
                    </div>
                  </div>
                ) : r.review_note ? <p className="text-xs text-gray-500 mt-1">Note: {r.review_note}</p> : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
